import { ReactNode } from 'react'

import { NextSeo } from 'next-seo'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

import { getBaseUrl } from '@/lib/contentful/utils'

import { isNonNullable } from '@/utils/isNonNullable'
import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import getLocalizedPathFromEnglish from '@/utils/locales/localized-routes/getLocalizedPathFromEnglish'
import { useUrl } from '@/utils/url/useUrl'

import { useEntry } from '../CaseStudy.hooks'

type Props = {
	children: ReactNode
}
export function CaseStudyMeta({ children }: Props) {
	const entry = useEntry()
	const { query: slug, locale = 'en-US' } = useRouter()
	const url = useUrl({ locale })

	const { t } = useTranslation('caseStudyDefaultSEO')

	const baseUrl = `${url?.protocol}//${url?.host}`

	if ('error' in entry) return <>{children}</>

	const defaultDescription = t('description')

	const {
		seoMetadata,
		status_en_US,
		status_en_GB,
		status_en_AU,
		status_it_IT,
		status_nl_NL,
		status_fr_FR,
		status_es_ES,
		status_de_DE,
		status_es_MX
	} = entry.data

	const includeTitleTemplate = Boolean(seoMetadata?.includeTitleTemplate)
	const title = includeTitleTemplate ? `${seoMetadata?.title} | BigCommerce` : seoMetadata?.title
	const description = seoMetadata?.description || defaultDescription || undefined

	return (
		<>
			<NextSeo
				title={title ?? undefined}
				description={description}
				canonical={
					seoMetadata?.canonicalUrl ||
					`${baseUrl}${getLocalizedPathFromEnglish(locale, '/case-study/')}${slug.slug}/`
				}
				languageAlternates={[
					status_es_MX
						? {
								hrefLang: 'es-MX',
								href: `${getBaseUrl('es-MX')}${getLocalizedPathFromEnglish(
									'es-MX',
									'/case-study/'
								)}${slug.slug}/`
						  }
						: null,
					status_de_DE
						? {
								hrefLang: 'de-DE',
								href: `${getBaseUrl('de-DE')}${getLocalizedPathFromEnglish(
									'de-DE',
									'/case-study/'
								)}${slug.slug}/`
						  }
						: null,
					status_es_ES
						? {
								hrefLang: 'es-ES',
								href: `${getBaseUrl('es-ES')}${getLocalizedPathFromEnglish(
									'es-ES',
									'/case-study/'
								)}${slug.slug}/`
						  }
						: null,
					status_fr_FR
						? {
								hrefLang: 'fr-FR',
								href: `${getBaseUrl('fr-FR')}${getLocalizedPathFromEnglish(
									'fr-FR',
									'/case-study/'
								)}${slug.slug}/`
						  }
						: null,
					status_nl_NL
						? {
								hrefLang: 'nl-NL',
								href: `${getBaseUrl('nl-NL')}${getLocalizedPathFromEnglish(
									'nl-NL',
									'/case-study/'
								)}${slug.slug}/`
						  }
						: null,
					status_it_IT
						? {
								hrefLang: 'it-IT',
								href: `${getBaseUrl('it-IT')}${getLocalizedPathFromEnglish(
									'it-IT',
									'/case-study/'
								)}${slug.slug}/`
						  }
						: null,
					status_en_AU
						? {
								hrefLang: 'en-AU',
								href: `${getBaseUrl('en-AU')}${getLocalizedPathFromEnglish(
									'en-AU',
									'/case-study/'
								)}${slug.slug}/`
						  }
						: null,
					status_en_GB
						? {
								hrefLang: 'en-GB',
								href: `${getBaseUrl('en-GB')}${getLocalizedPathFromEnglish(
									'en-GB',
									'/case-study/'
								)}${slug.slug}/`
						  }
						: null,
					status_en_US
						? {
								hrefLang: 'en-US',
								href: `${getBaseUrl('en-US')}/case-study/${slug.slug}/`
						  }
						: null,
					status_en_US
						? {
								hrefLang: 'x-default',
								href: `${getBaseUrl('en-US')}/case-study/${slug.slug}/`
						  }
						: null
				].filter(isNonNullable)}
				openGraph={{
					title: title ?? undefined,
					description,
					url:
						seoMetadata?.canonicalUrl ||
						`${baseUrl}${getLocalizedPathFromEnglish(locale, '/case-study/')}${slug.slug}/`,
					images: seoMetadata?.image?.url
						? [
								{
									url: seoMetadata?.image?.url,
									width: seoMetadata?.image?.width,
									height: seoMetadata?.image?.height,
									alt: 'Bigcommerce logo',
									type: 'image/jpeg'
								}
						  ]
						: undefined,
					locale: getSeoLocale(locale)
				}}
				nofollow={Boolean(seoMetadata?.nofollow)}
				noindex={Boolean(seoMetadata?.noindex)}
			/>

			{children}
		</>
	)
}
